"use client";

import { useState, useLayoutEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Eye, EyeOff, Lock, ArrowRight, Mail, AlertCircle, KeyRound } from "lucide-react";
import { Loading } from "@/components/ui/loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { AuthService } from "@/services/auth-service";
import { onboardingService } from "@/services/onboarding-service";
import { Typewriter } from 'react-simple-typewriter';
import { motion } from "framer-motion";
import Image from "next/image";
import { APP_LOGO_PATH } from "@/lib/constants";

// Schema de Validação
const loginSchema = z.object({
    email: z.string().min(1, "E-mail é obrigatório").email("Formato de e-mail inválido"),
    password: z.string().min(6, "A senha deve ter no mínimo 6 caracteres"),
    rememberMe: z.boolean().optional().default(false),
});

const chaveAcessoSchema = z.object({
    chave: z.string().min(1, "Insira sua chave de acesso"),
});

type LoginFormData = z.infer<typeof loginSchema>;
type ChaveAcessoFormData = z.infer<typeof chaveAcessoSchema>;

const WHATSAPP_CONSULTOR = "https://wa.me/5511999999999";

export default function LoginPage() {
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [shakeError, setShakeError] = useState(false); // Controls the shake animation
    const [modoChaveAcesso, setModoChaveAcesso] = useState(false);
    const router = useRouter();

    // Login sempre em tema escuro (useLayoutEffect evita flash antes da pintura)
    useLayoutEffect(() => {
        document.documentElement.setAttribute("data-theme", "dark");
    }, []);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
    });

    const {
        register: registerChave,
        handleSubmit: handleSubmitChave,
        formState: { errors: errorsChave },
    } = useForm<ChaveAcessoFormData>({
        resolver: zodResolver(chaveAcessoSchema),
    });

    const onSubmit = async (data: LoginFormData) => {
        setIsLoading(true);
        setShakeError(false); // Reset shake

        try {
            const response = await AuthService.login({
                email: data.email,
                password: data.password,
            });

            const userPayload = {
                id: response.id,
                nome: response.nome,
                email: response.email,
                role: response.role,
                concessionariaNome: response.concessionariaNome || null,
                concessionariaId: response.concessionariaId || null
            };

            // Persistência condicional: sessão curta vs manter conectado
            const remember = data.rememberMe ?? false;
            if (remember) {
                localStorage.setItem("token", response.token);
                localStorage.setItem("user", JSON.stringify(userPayload));
            } else {
                sessionStorage.setItem("token", response.token);
                sessionStorage.setItem("user", JSON.stringify(userPayload));
            }

            // Ajustar cookie de acordo com a opção
            if (typeof window !== "undefined") {
                const maxAge = remember ? 60 * 60 * 24 * 30 : 60 * 60 * 4; // 30 dias vs 4h
                document.cookie = `token=${response.token}; path=/; max-age=${maxAge}; SameSite=Strict`;
            }

            toast.success(`Bem-vindo, ${response.nome}!`, {
                description: "Acesso autorizado com sucesso.",
                duration: 4000
            });
            router.push("/dashboard");

        } catch (error: any) {
            console.error("Login Error:", error);

            setShakeError(true);
            setTimeout(() => setShakeError(false), 500);

            const code = error?.code;
            const isNetwork = code === "ERR_NETWORK" || error?.message?.includes("Network Error") || error?.message?.includes("Failed to fetch");

            if (isNetwork) {
                toast.error("Erro de conexão", {
                    description: "Não foi possível conectar ao servidor. Verifique sua internet ou tente novamente em instantes.",
                    duration: 6000,
                });
            } else {
                const msg = error.response?.data?.message || "Credenciais inválidas. Verifique seu e-mail e senha.";
                toast.error("Acesso Negado", {
                    description: msg,
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const onValidarChave = async (data: ChaveAcessoFormData) => {
        setIsLoading(true);
        setShakeError(false);
        try {
            const response = await onboardingService.validateInviteToken(data.chave.trim());
            const params = new URLSearchParams({
                token: data.chave.trim(),
                clientName: response.clientName ?? "Cliente",
            });
            router.push(`/setup-account?${params.toString()}`);
        } catch (error: any) {
            const msg = error.response?.data?.message || "Chave inválida ou expirada.";
            setShakeError(true);
            setTimeout(() => setShakeError(false), 500);
            toast.error("Chave inválida", { description: msg });
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDownVoltar = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setModoChaveAcesso(false);
        }
    };

    const handleKeyDownChaveLink = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setModoChaveAcesso(true);
        }
    };

    return (
        <div className="relative flex min-h-dvh w-full overflow-x-hidden bg-black text-white selection:bg-primary selection:text-primary-foreground">

            {/* Background Texture/Noise */}
            <div className="pointer-events-none absolute inset-0 z-0 mix-blend-overlay opacity-[0.03] bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />

            {/* LADO ESQUERDO - VISUAL "NO WAY" (Desktop Only) */}
            <div className="relative hidden w-[50%] items-center justify-center overflow-hidden border-r border-white/5 bg-black lg:flex">

                {/* Car Background */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&q=80"
                        alt="Luxury Car Background"
                        className="w-full h-full object-cover opacity-100 scale-105 saturate-0"
                    />
                    {/* TRUE BLACK OVERLAY */}
                    <div className="absolute inset-0 bg-black/80" />
                    <div className="absolute inset-0 bg-linear-to-t from-black via-transparent to-transparent" />
                </div>

                <div className="relative z-10 max-w-2xl px-12 text-center flex flex-col items-center">
                    {/* Logo */}
                    <motion.div
                        animate={{ y: [0, -10, 0] }}
                        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                        className="mb-8 relative w-64 h-24"
                    >
                        <Image
                            src={APP_LOGO_PATH}
                            alt="NoWey Logo"
                            fill
                            className="object-contain drop-shadow-2xl"
                            priority
                        />
                    </motion.div>

                    <h1 className="text-5xl font-black text-white mb-6 tracking-tighter leading-[1.1] min-h-[160px] drop-shadow-2xl">
                        <span className="block text-gray-400 mb-3 font-bold tracking-widest text-xl uppercase font-sans">
                            <Typewriter
                                words={['Ainda usa planilhas?', 'Prejuízo no caixa?', 'Gestão manual?', 'Perder vendas?']}
                                loop={0}
                                cursor
                                cursorStyle='_'
                                typeSpeed={60}
                                deleteSpeed={40}
                                delaySpeed={2000}
                            />
                        </span>
                        <span className="block text-5xl mb-2 text-white">No way...</span>
                        <span className="text-primary drop-shadow-[0_0_15px_var(--primary)]">Use NoWey.</span>
                    </h1>
                </div>
            </div>

            {/* LADO DIREITO - LOGIN FORM (Pitch Black Glass) */}
            <div className="relative flex min-h-dvh w-full flex-1 flex-col items-stretch justify-center bg-black px-4 py-6 sm:px-6 sm:py-8 lg:w-[50%] lg:items-center lg:justify-center lg:px-8 lg:py-10 supports-[padding:max(0px)]:pb-[max(1.25rem,env(safe-area-inset-bottom))] supports-[padding:max(0px)]:pt-[max(0.75rem,env(safe-area-inset-top))]">
                {/* Gradiente sutil no canto (desktop + mobile) */}
                <div className="pointer-events-none absolute inset-0 bg-linear-to-br from-primary/5 via-transparent to-transparent" />
                <div className="pointer-events-none absolute top-0 right-0 h-[min(70vw,28rem)] w-[min(100vw,32rem)] rounded-full bg-primary/10 blur-[100px] sm:h-[500px] sm:w-[500px] sm:blur-[120px] lg:hidden" />

                <motion.div
                    animate={shakeError ? { x: [-10, 10, -10, 10, 0] } : {}}
                    transition={{ duration: 0.4 }}
                    className="relative mx-auto w-full max-w-[420px] overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0c0c14]/95 p-5 shadow-2xl shadow-black/50 backdrop-blur-xl sm:rounded-3xl sm:p-8 md:p-10 lg:rounded-[2.5rem] lg:p-12"
                >
                    {/* Borda superior sutil + brilho */}
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/20 to-transparent" />
                    <div className="pointer-events-none absolute -left-[50%] -top-[50%] h-[200%] w-[200%] rotate-45 bg-linear-to-br from-primary/5 via-transparent to-transparent" />

                    {/* Top Logo (Mobile Only) */}
                    <div className="mb-6 flex justify-center sm:mb-8 lg:hidden">
                        <div className="relative h-8 w-28 opacity-90 sm:h-9 sm:w-32">
                            <Image
                                src={APP_LOGO_PATH}
                                alt="NoWey Logo"
                                fill
                                className="object-contain"
                            />
                        </div>
                    </div>

                    <div className="relative z-10 mb-8 text-center sm:mb-10">
                        <h2 className="mb-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">Bem-vindo</h2>
                        <p className="text-sm font-medium text-zinc-400 px-1">
                            {modoChaveAcesso ? "Insira sua chave de acesso para continuar." : "Acesse o painel premium da sua concessionária."}
                        </p>
                    </div>

                    {!modoChaveAcesso ? (
                    <form onSubmit={handleSubmit(onSubmit)} className="relative z-10 space-y-5 sm:space-y-6">

                        <div className="space-y-2">
                            <Label htmlFor="email" className="pl-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 sm:pl-4">E-mail Corporativo</Label>
                            <div className="group relative">
                                <div className="pointer-events-none absolute left-4 top-1/2 z-[1] -translate-y-1/2 text-zinc-500 transition-colors group-focus-within:text-primary sm:left-6">
                                    <Mail className="h-4 w-4 sm:h-5 sm:w-5" />
                                </div>
                                <Input
                                    id="email"
                                    type="email"
                                    autoComplete="email"
                                    inputMode="email"
                                    placeholder="seu.nome@concessionaria.com"
                                    className="h-14 rounded-full border-white/[0.08] bg-white/[0.04] pl-12 text-base text-white placeholder:text-zinc-500 transition-all hover:border-white/[0.12] hover:bg-white/[0.06] focus:border-primary/60 focus:ring-2 focus:ring-primary/20 sm:h-16 sm:pl-14"
                                    {...register("email")}
                                />
                            </div>
                            {errors.email && <span className="mt-1 flex items-center gap-1 pl-3 text-xs text-red-500 sm:pl-4"><AlertCircle className="h-3 w-3 shrink-0" /> {errors.email.message}</span>}
                        </div>

                        <div className="space-y-2">
                            <div className="flex flex-col gap-2 px-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
                                <Label htmlFor="password" className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Senha</Label>
                                <Link href="#" className="text-xs font-medium text-zinc-400 transition-colors hover:text-primary self-start sm:self-auto">Esqueceu a senha?</Link>
                            </div>
                            <div className="group relative">
                                <div className="pointer-events-none absolute left-4 top-1/2 z-[1] -translate-y-1/2 text-zinc-500 transition-colors group-focus-within:text-primary sm:left-6">
                                    <Lock className="h-4 w-4 sm:h-5 sm:w-5" />
                                </div>
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    className="h-14 rounded-full border-white/[0.08] bg-white/[0.04] pl-12 pr-12 text-base text-white placeholder:text-zinc-500 transition-all hover:border-white/[0.12] hover:bg-white/[0.06] focus:border-primary/60 focus:ring-2 focus:ring-primary/20 sm:h-16 sm:pl-14 sm:pr-14"
                                    {...register("password")}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-zinc-500 transition-colors hover:text-white sm:right-5 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                                >
                                    <motion.div
                                        key={showPassword ? "visible" : "hidden"}
                                        initial={{ opacity: 0, scale: 0.8, rotate: -20 }}
                                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                                        exit={{ opacity: 0, scale: 0.8, rotate: 20 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                                    </motion.div>
                                </button>
                            </div>
                            {errors.password && <span className="mt-1 flex items-center gap-1 pl-3 text-xs text-red-500 sm:pl-4"><AlertCircle className="h-3 w-3 shrink-0" /> {errors.password.message}</span>}
                        </div>

                        <div className="flex items-center justify-between px-3 text-xs text-zinc-400 sm:px-4">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-white/30 bg-transparent text-primary focus:ring-primary/40 focus:ring-2"
                                    {...register("rememberMe")}
                                />
                                <span className="font-medium">Manter conectado</span>
                            </label>
                        </div>

                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="mt-1 h-14 w-full min-h-[48px] rounded-full bg-primary text-sm font-bold uppercase tracking-widest text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:bg-primary/90 hover:shadow-primary/40 active:scale-[0.98] sm:h-16 sm:mt-2"
                        >
                            {isLoading ? <Loading variant="inline" /> : <span className="flex items-center justify-center gap-2">Entrar no Sistema <ArrowRight className="h-4 w-4" /></span>}
                        </Button>
                    </form>

                    ) : (
                    <form onSubmit={handleSubmitChave(onValidarChave)} className="relative z-10 space-y-5 sm:space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="chave" className="pl-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 sm:pl-4">Chave de Acesso</Label>
                            <div className="group relative">
                                <div className="pointer-events-none absolute left-4 top-1/2 z-[1] -translate-y-1/2 text-zinc-500 transition-colors group-focus-within:text-primary sm:left-6">
                                    <KeyRound className="h-4 w-4 sm:h-5 sm:w-5" />
                                </div>
                                <Input
                                    id="chave"
                                    type="text"
                                    autoComplete="one-time-code"
                                    placeholder="Cole ou digite sua chave"
                                    className="h-14 rounded-full border-white/[0.08] bg-white/[0.04] pl-12 text-base text-white placeholder:text-zinc-500 transition-all hover:border-white/[0.12] hover:bg-white/[0.06] focus:border-primary/60 focus:ring-2 focus:ring-primary/20 sm:h-16 sm:pl-14"
                                    aria-label="Chave de acesso"
                                    {...registerChave("chave")}
                                />
                            </div>
                            {errorsChave.chave && <span className="mt-1 flex items-center gap-1 pl-3 text-xs text-red-500 sm:pl-4"><AlertCircle className="h-3 w-3 shrink-0" /> {errorsChave.chave.message}</span>}
                        </div>
                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="mt-1 h-14 w-full min-h-[48px] rounded-full bg-primary text-sm font-bold uppercase tracking-widest text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:bg-primary/90 hover:shadow-primary/40 active:scale-[0.98] sm:h-16 sm:mt-2"
                        >
                            {isLoading ? <Loading variant="inline" /> : <span className="flex items-center justify-center gap-2">Validar Chave <ArrowRight className="h-4 w-4" /></span>}
                        </Button>
                    </form>
                    )}

                    <div className="relative z-10 mt-6 text-center sm:mt-8">
                        {modoChaveAcesso ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setModoChaveAcesso(false)}
                                    onKeyDown={handleKeyDownVoltar}
                                    tabIndex={0}
                                    aria-label="Voltar para login com e-mail e senha"
                                    className="text-sm text-zinc-400 hover:text-primary transition-colors font-medium"
                                >
                                    ← Voltar
                                </button>
                                <p className="text-xs text-zinc-500 mt-4">
                                    Não tem conta? <a href={WHATSAPP_CONSULTOR} target="_blank" rel="noopener noreferrer" className="text-white hover:text-primary hover:underline underline-offset-4">Fale com um consultor</a>
                                </p>
                            </>
                        ) : (
                        <>
                        <p className="text-xs leading-relaxed text-zinc-500">
                            Possui uma Chave de Acesso?{" "}
                            <button
                                type="button"
                                onClick={() => setModoChaveAcesso(true)}
                                onKeyDown={handleKeyDownChaveLink}
                                tabIndex={0}
                                aria-label="Validar chave de acesso"
                                className="text-white hover:text-primary hover:underline underline-offset-4 font-bold"
                            >
                                Clique aqui
                            </button>
                        </p>
                        <p className="mt-2 text-xs leading-relaxed text-zinc-500">
                            Ainda não é cliente NoWey?{" "}
                            <Link href="/#contato" className="font-bold text-white underline decoration-primary/30 underline-offset-4 hover:text-primary hover:underline">
                                Comece Agora
                            </Link>
                        </p>
                        </>
                        )}
                    </div>

                    <div className="relative z-10 mt-6 border-t border-white/[0.06] pt-5 text-center sm:mt-8 sm:pt-6">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 px-2">
                            Protected by End-to-End Encryption
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
